import { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import BASE_URL from "../../../../assets/global/url";
import { useServerPagination } from "../../../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../../../hooks/customHook/paginationHook/usePagination";
import { customStyles } from "../../../../assets/table-style";
import DateRangePicker from "../../../../components/DateRangePicker";
import { formatToTwoDecimal } from "../../../../utils/numberFormatter"

const CUSTOMER_FILTER_OPTIONS = [
  { label: "All",           value: "all" },
  { label: "TIN",           value: "tin" },
  { label: "Customer Name", value: "customer_name" },
  { label: "Company Name",  value: "company_name" },
  { label: "Phone",         value: "mobile_no" },
  { label: "Email",         value: "email" },
  { label: "Country",       value: "country" },
  { label: "Type",          value: "type" },
];

const customerColumns = [
  {
    name: "Tin",
    selector: (row) => row.tin || "--",
    wrap: true,
    grow: 1,
  },
  {
    name: "Customer Name",
    selector: (row) =>
      row.type === "individual"
        ? `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() || "--"
        : "--",
    wrap: true,
    grow: 2,
  },
  {
    name: "Company Name",
    selector: (row) =>
      row.type === "company" ? row.company_name || "--" : "--",
    wrap: true,
    grow: 2,
  },
  {
    name: "Phone",
    selector: (row) => row.mobile_no || "--",
    wrap: true,
    grow: 1,
  },
  {
    name: "Email",
    selector: (row) => row.email || "--",
    wrap: true,
    grow: 2,
  },
  {
    name: "Country",
    selector: (row) => row.country || "--",
    wrap: true,
    grow: 1,
  },
  {
    name: "Type",
    selector: (row) => row.type || "--",
    wrap: true,
    grow: 1,
  },
  {
    name: "Status",
    selector: (row) => (row.status === true ? "Active" : "Inactive"),
    cell: (row) => {
      const statusLabel = row.status === true ? "Active" : "Inactive";
      return (
        <div
          style={{
            padding: "5px 10px",
            borderRadius: "5px",
            color: statusLabel === "Active" ? "#3B9F3F" : "#FFA500",
            textTransform: "uppercase",
            fontWeight: "bold",
          }}
        >
          {statusLabel}
        </div>
      );
    },
    wrap: true,
    grow: 1,
  },
];

const TRANSACTION_FILTER_OPTIONS = [
  { label: "All",              value: "all" },
  { label: "Transaction ID",   value: "transaction_id" },
  { label: "Destination",      value: "destination" },
  { label: "Container Number", value: "container_number" },
  { label: "Payment Terms",    value: "payment_terms" },
];

const buildTransactionColumns = (selectedTransactions, onToggleRow, onToggleAll, visibleData) => {
  const allVisibleSelected =
    visibleData.length > 0 &&
    visibleData.every((row) =>
      selectedTransactions.some((t) => t.sales_invoice_id === row.sales_invoice_id)
    );

  return [
    {
      name: (
        <input
          type="checkbox"
          checked={allVisibleSelected}
          onChange={onToggleAll}
          title="Select / Deselect All"
        />
      ),
      cell: (row) => (
        <input
          type="checkbox"
          checked={selectedTransactions.some(
            (t) => t.sales_invoice_id === row.sales_invoice_id
          )}
          onChange={() => onToggleRow(row)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      width: "50px",
      ignoreRowClick: true,
    },
    {
      name: "Transaction ID",
      selector: (row) => row.transaction_id || "--",
      wrap: true,
      grow: 2,
    },
    {
      name: "Destination",
      selector: (row) => row.destination || "--",
      wrap: true,
      grow: 2,
    },
    {
      name: "Container Number",
      selector: (row) => row.container_number || "--",
      wrap: true,
      grow: 2,
    },
    {
      name: "Invoice Date",
      selector: (row) =>
        row.invoice_date
          ? new Date(row.invoice_date).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : "--",
      wrap: true,
      grow: 1,
    },
    {
      name: "Payment Terms",
      selector: (row) => row.payment_terms || "--",
      wrap: true,
      grow: 2,
    },
    {
      name: "Total Amount",
      selector: (row) =>
        row.total_amount != null
          ? formatToTwoDecimal(row.total_amount)
          : "--",
      wrap: true,
      grow: 1,
    },
  ];
};


const tableStyles = {
  ...customStyles,
  rows: {
    ...customStyles?.rows,
    style: {
      ...(customStyles?.rows?.style ?? {}),
      cursor: "pointer",
    },
    highlightOnHoverStyle: {
      backgroundColor: "#2D2C67",
      color: "#ffffff",
      borderBottomColor: "#2D2C67",
      outline: "none",
    },
  },
};

// ─── COMPONENT ────────────────────────────────────────────────────────────────

const InvoiceReOpenModal = () => {
  const [firstModal, setFirstModal] = useState(false);
  const [searchTextCustomer, setSearchCustomer] = useState("");
  const [filterColumn, setFilterColumn] = useState("all");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const selectedCustomerRef = useRef(null);

  // ── Second Modal ──
  const [secondModal, setSecondModal] = useState(false);
  const [searchTextTransaction,setSearchTextTransaction] = useState("");
  const [txFilterColumn, setTxFilterColumn] = useState("all");
  const [txDropdownOpen, setTxDropdownOpen] = useState(false);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });

  const debounceTimer = useRef(null);
  const dropdownRef   = useRef(null);
  const txDropdownRef = useRef(null);
  const tableRef      = useRef(null);
  const firstModalRef = useRef(false);

  useEffect(() => {
    firstModalRef.current = firstModal;
  }, [firstModal]);

  const customerData = useServerPagination(
    `${BASE_URL}/invoice/getCustomersWithApprovedInvoices`,
    10,
  );

  const transactionsPagination = useServerPagination(
    `${BASE_URL}/invoice/fetchSpecificInvoiceTransaction`,
    10,
    { customerId: null, searchText: "", filterColumn: "all" },
  );

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
      if (txDropdownRef.current && !txDropdownRef.current.contains(e.target))
        setTxDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (
        firstModalRef.current &&
        tableRef.current &&
        !tableRef.current.contains(e.target)
      ) {
        const modalFooter = document.querySelector(".modal.show .modal-footer");
        const modalBody   = document.querySelector(".modal.show .modal-body");
        const inModal =
          (modalFooter && modalFooter.contains(e.target)) ||
          (modalBody   && modalBody.contains(e.target));

        if (inModal && !modalFooter?.contains(e.target)) {
          selectedCustomerRef.current = null;
          setSelectedCustomer(null);
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // FIRST MODAL HANDLERS
  // ─────────────────────────────────────────────────────────────────────────

  const handleSearchCustomer = (value) => {
    const raw = String(value).replace(/,/g, "");
    setSearchCustomer(value);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      customerData.updateParams({ searchText: raw, filterColumn });
    }, 500);
  };

  const handleFilterSelect = (value) => {
    setFilterColumn(value);
    setDropdownOpen(false);
    customerData.updateParams({
      searchText: String(searchTextCustomer).replace(/,/g, ""),
      filterColumn: value,
    });
  };

  const resetFirstModal = () => {
    setFilterColumn("all");
    setSearchCustomer("");
    setDropdownOpen(false);
    selectedCustomerRef.current = null;
    setSelectedCustomer(null);
  };

  const handleFirstModalClose = () => {
    setFirstModal(false);
    resetFirstModal();
  };

  const handleFirstModalShow = () => {
    setFirstModal(true);
    resetFirstModal();
  };

  const handleRowClickCustomer = (row) => {
    const next =
      selectedCustomerRef.current?.customer_id === row.customer_id ? null : row;
    selectedCustomerRef.current = next;
    setSelectedCustomer(next);
  };


  const handleProceed = () => {
    const customer = selectedCustomerRef.current;
    if (!customer) return;

    setFirstModal(false);

    transactionsPagination.updateParams({
      customerId:   customer.customer_id,
      searchText:   "",
      filterColumn: "all",
      startDate:    null,
      endDate:      null,
    });

    setSearchTextTransaction("");
    setTxFilterColumn("all");
    setTxDropdownOpen(false);
    setSelectedTransactions([]);
    setDateRange({ startDate: null, endDate: null });

    setSecondModal(true);
  };

  const activeCustomerFilterLabel =
    CUSTOMER_FILTER_OPTIONS.find((o) => o.value === filterColumn)?.label ?? "All";

  const customerConditionalRowStyles = [
    {
      when: (row) => row.customer_id === selectedCustomer?.customer_id,
      style: {
        backgroundColor: "#2D2C67",
        color: "#ffffff",
      },
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────
  // SECOND MODAL HANDLERS
  // ─────────────────────────────────────────────────────────────────────────

  const handleSecondModalClose = () => {
    setSecondModal(false);
    setSearchTextTransaction("");
    setTxFilterColumn("all");
    setTxDropdownOpen(false);
    setSelectedTransactions([]);
    setDateRange({ startDate: null, endDate: null });
  };

  const handleBack = () => {
    setSecondModal(false);
    setFirstModal(true);
    setSearchTextTransaction("");
    setTxFilterColumn("all");
    setTxDropdownOpen(false);
    setSelectedTransactions([]);
    setDateRange({ startDate: null, endDate: null });
  };

  const handleSearchTransaction = (value) => {
    setSearchTextTransaction(value);
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      transactionsPagination.updateParams({
        customerId:   selectedCustomerRef.current?.customer_id,
        searchText:   value,
        filterColumn: txFilterColumn,
        startDate:    dateRange.startDate,
        endDate:      dateRange.endDate,
      });
    }, 500);
  };

  const handleTxFilterSelect = (value) => {
    setTxFilterColumn(value);
    setTxDropdownOpen(false);
    transactionsPagination.updateParams({
      customerId:   selectedCustomerRef.current?.customer_id,
      searchText:   searchTextTransaction,
      filterColumn: value,
      startDate:    dateRange.startDate,
      endDate:      dateRange.endDate,
    });
  };

  const handleDateRangeChange = (startDate, endDate) => {
    setDateRange({ startDate, endDate });
    transactionsPagination.updateParams({
      customerId:   selectedCustomerRef.current?.customer_id,
      searchText:   searchTextTransaction,
      filterColumn: txFilterColumn,
      startDate,
      endDate,
    });
  };

  const handleToggleRow = (row) => {
    setSelectedTransactions((prev) => {
      const exists = prev.some((t) => t.sales_invoice_id === row.sales_invoice_id);
      return exists
        ? prev.filter((t) => t.sales_invoice_id !== row.sales_invoice_id)
        : [...prev, row];
    });
  };

  const handleToggleAll = () => {
    const allVisible = transactionsPagination.data ?? [];
    const allSelected = allVisible.every((row) =>
      selectedTransactions.some((t) => t.sales_invoice_id === row.sales_invoice_id)
    );
    if (allSelected) {
      setSelectedTransactions((prev) =>
        prev.filter(
          (t) => !allVisible.some((r) => r.sales_invoice_id === t.sales_invoice_id)
        )
      );
    } else {
      setSelectedTransactions((prev) => {
        const existing = new Set(prev.map((t) => t.sales_invoice_id));
        const toAdd = allVisible.filter((r) => !existing.has(r.sales_invoice_id));
        return [...prev, ...toAdd];
      });
    }
  };

  const transactionColumns = buildTransactionColumns(
    selectedTransactions,
    handleToggleRow,
    handleToggleAll,
    transactionsPagination.data ?? [],
  );

  const activeTxFilterLabel =
    TRANSACTION_FILTER_OPTIONS.find((o) => o.value === txFilterColumn)?.label ?? "All";

  const customerDisplayName =
    selectedCustomer?.type === "company"
      ? selectedCustomer?.company_name || "--"
      : `${selectedCustomer?.first_name ?? ""} ${selectedCustomer?.last_name ?? ""}`.trim() || "--";


  return (
    <>
      <Button
        variant="primary"
        className="d-flex flex-row align-items-center pt-2 px-3 me-2"
        onClick={handleFirstModalShow}
      >
        Re-Open
      </Button>

      {/* ── 1st MODAL: Customer List ── */}
      <Modal
        show={firstModal}
        onHide={handleFirstModalClose}
        backdrop="static"
        size="xl"
        centered
        scrollable={true}
        dialogClassName="payable-reopen-modal-size"
      >
        <Modal.Header closeButton>
          <Modal.Title>Customer Lists</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap align-items-end mb-3 gap-2">
            <div
              className="input-group"
              ref={dropdownRef}
              style={{ position: "relative" }}
            >
              <input
                type="text"
                className="form-control"
                placeholder={`Search by ${activeCustomerFilterLabel}...`}
                value={searchTextCustomer}
                onChange={(e) => handleSearchCustomer(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setDropdownOpen((prev) => !prev)}
                title="Filter by column"
              >
                <i className="fa-solid fa-sliders"></i>
              </button>

              {dropdownOpen && (
                <ul
                  className="dropdown-menu show"
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    zIndex: 1055,
                    minWidth: "180px",
                  }}
                >
                  {CUSTOMER_FILTER_OPTIONS.map((opt) => (
                    <li key={opt.value}>
                      <button
                        type="button"
                        className={`dropdown-item ${filterColumn === opt.value ? "active" : ""}`}
                        onClick={() => handleFilterSelect(opt.value)}
                      >
                        {opt.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div ref={tableRef}>
            <DataTable
              columns={customerColumns}
              data={customerData.data}
              onRowClicked={handleRowClickCustomer}
              customStyles={tableStyles}
              conditionalRowStyles={customerConditionalRowStyles}
              highlightOnHover
              pointerOnHover
              responsive
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <PaginationControls {...customerData} />
            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={handleFirstModalClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!selectedCustomer}
                onClick={handleProceed}
              >
                Proceed
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ── 2nd MODAL: Invoice Transaction List ── */}
      <Modal
        show={secondModal}
        onHide={handleSecondModalClose}
        backdrop="static"
        size="xl"
        centered
        scrollable={true}
        dialogClassName="payable-reopen-modal-size"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Invoice Transactions List:{" "}
            <span className="text-primary">{customerDisplayName}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-end mb-3 gap-2">
            <div style={{ minWidth: "250px" }}>
              <DateRangePicker
                label="Invoice Date"
                onDateRangeChange={handleDateRangeChange}
              />
            </div>

            <div
              className="input-group"
              ref={txDropdownRef}
              style={{ position: "relative", maxWidth: "400px" }}
            >
              <input
                type="text"
                className="form-control"
                placeholder={`Search by ${activeTxFilterLabel}...`}
                value={searchTextTransaction}
                onChange={(e) => handleSearchTransaction(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setTxDropdownOpen((prev) => !prev)}
                title="Filter by column"
              >
                <i className="fa-solid fa-sliders"></i>
              </button>

              {txDropdownOpen && (
                <ul
                  className="dropdown-menu show"
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    zIndex: 1055,
                    minWidth: "200px",
                  }}
                >
                  {TRANSACTION_FILTER_OPTIONS.map((opt) => (
                    <li key={opt.value}>
                      <button
                        type="button"
                        className={`dropdown-item ${txFilterColumn === opt.value ? "active" : ""}`}
                        onClick={() => handleTxFilterSelect(opt.value)}
                      >
                        {opt.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <DataTable
            columns={transactionColumns}
            data={transactionsPagination.data || []}
            highlightOnHover
            pointerOnHover
            customStyles={tableStyles}
            responsive
          />
        </Modal.Body>
        <Modal.Footer>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <PaginationControls {...transactionsPagination} />
            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={handleBack}>
                Back
              </Button>
              <Link
                to="/sales/transaction-invoice-list"
                state={{
                  transactions: selectedTransactions.map((t) => t.sales_invoice_id),
                  customer: selectedCustomer,
                }}
                style={{
                  pointerEvents: selectedTransactions.length === 0 ? "none" : "auto",
                }}
              >
                <Button
                  variant="primary"
                  disabled={selectedTransactions.length === 0}
                >
                  Proceed
                </Button>
              </Link>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InvoiceReOpenModal;